// dev-portal/src/pages/auth/Signup.jsx

import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../../api/client';
import Footer from '../../components/shared/Footer';
import { initializeDentuluProButton } from '../../utils/dentuluProSSO';
import { USER_ROLES } from '../../constants/userRoles';
import PasswordValidation, { isPasswordValid, validatePassword } from '../../components/shared/PasswordValidation';
import { loadRecaptcha, executeRecaptcha, showRecaptchaBadge, hideRecaptchaBadge } from '../../utils/recaptcha';
import InviteSignup from './InviteSignup';

export default function Signup() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');

  const [selectedRole, setSelectedRole] = useState('');
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    company_name: '',
    website: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Initialize Dentulu Pro button and load reCAPTCHA on mount
  useEffect(() => {
    if (inviteToken) return; // invite acceptance is a separate, simpler form
    const cleanup = initializeDentuluProButton('dentuluprologinbtn', 'signup');
    // Load reCAPTCHA script and show badge
    loadRecaptcha().catch(err => console.warn('Failed to load reCAPTCHA:', err));
    showRecaptchaBadge();
    return () => {
      cleanup();
      hideRecaptchaBadge();
    };
  }, [inviteToken]);

  if (inviteToken) {
    return <InviteSignup token={inviteToken} />;
  }

  const roles = USER_ROLES;

  // Check if selected role is developer/software company
  const isDeveloperRole = selectedRole === 'developer_software_company';

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // Email validation helper
  const getEmailLocalPart = (email) => {
    if (!email.includes('@')) return email;
    return email.split('@')[0];
  };
  const emailLocalPart = getEmailLocalPart(form.email);
  const isEmailLocalPartTooLong = emailLocalPart.length > 64;

  // Handle role selection change
  const handleRoleChange = (roleId) => {
    setSelectedRole(roleId);

    // If switching away from developer role, clear company fields
    if (roleId !== 'developer_software_company') {
      setForm(prev => ({
        ...prev,
        company_name: '',
        website: ''
      }));
    }
  };

  const checkPasswordValid = (password) => {
    const errors = validatePassword(password);
    if (errors.length > 0) {
      return errors[0]; // Return first error
    }
    return null;
  };

  const validateForm = () => {
    if (!form.full_name.trim()) {
      setError('Full name is required');
      return false;
    }
    if (!form.email.includes('@')) {
      setError('Enter a valid email address');
      return false;
    }
    // Validate email local part (before @) max 64 characters
    const localPart = form.email.split('@')[0];
    if (localPart.length > 64) {
      setError('Email address before @ cannot exceed 64 characters');
      return false;
    }
    if (!selectedRole) {
      setError('Please select your role');
      return false;
    }

    // Only validate company fields for developer role
    if (isDeveloperRole) {
      if (!form.company_name.trim()) {
        setError('Company name is required for developers');
        return false;
      }
    }

    const passwordError = checkPasswordValid(form.password);
    if (passwordError) {
      setError(passwordError);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('SIGNUP');
      
      // Prepare submission data
      const submitData = {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        role: selectedRole,
        recaptcha_token: recaptchaToken
      };

      // Only include company fields if developer role
      if (isDeveloperRole) {
        submitData.company_name = form.company_name;
        submitData.website = form.website || '';
      }
      // For non-developer roles, company_name can be blank (not sent)

      await api.post('/api/register', submitData);

      setSuccess('Account created! Awaiting admin approval. You will receive an email once approved.');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Signup failed';
      if (errorMessage.includes('reCAPTCHA')) {
        setError('Security verification failed. Please try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg px-8 pt-6 pb-8 w-full max-w-md">
          {/* Header Links */}
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Marketplace
            </Link>
            <Link to="/portal" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              Developer Portal
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
            Create Account
          </h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                type="text"
                name="full_name"
                placeholder="Full Name *"
                value={form.full_name}
                onChange={handleChange}
                maxLength={30}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{form.full_name.length}/30 characters</p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white ${
                  isEmailLocalPartTooLong ? 'border-red-500' : 'border-gray-300'
                }`}
                type="email"
                name="email"
                placeholder="Email *"
                value={form.email}
                onChange={handleChange}
                required
              />
              {isEmailLocalPartTooLong && (
                <p className="text-xs mt-1 text-red-600">
                  The email address is invalid. The part before the "@" cannot exceed 64 characters.
                </p>
              )}
            </div>

            {/* Select Your Role */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-3">
                Select Your Role <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition ${selectedRole === role.id
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                      }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.id}
                      checked={selectedRole === role.id}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium">{role.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Password <span className="text-red-500">*</span>
                <span className="text-gray-500 font-normal"> (8-12 characters)</span>
              </label>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 text-gray-900 bg-white"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password * (8-12 characters)"
                  value={form.password}
                  onChange={handleChange}
                  maxLength={12}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>
              </div>
              <PasswordValidation password={form.password} />
            </div>

            {/* Company Name - Only show for Developer role */}
            {isDeveloperRole && (
              <div className="border-t pt-4">
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                    type="text"
                    name="company_name"
                    placeholder="Company Name *"
                    value={form.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Website - Only show for Developer role */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Website (optional)
                  </label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                    type="text"
                    name="website"
                    placeholder="https://yourcompany.com"
                    value={form.website}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Sign Up Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition-colors font-semibold mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          {/* Already have account */}
          <div className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              Login
            </Link>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          {/* SSO Options */}
          <div className="space-y-3">
            {/* Dentulu Pro SSO Button */}
            <a
              href="#"
              id="dentuluprologinbtn"
              className="block w-full bg-white border-2 border-blue-500 hover:bg-blue-50 hover:border-blue-600 transition-all rounded-lg overflow-hidden shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-center py-3 px-4">
                <img
                  src="/dentulu-pro-icon.png"
                  alt="Dentulu Pro"
                  className="h-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (!parent.querySelector('.fallback-text')) {
                      const span = document.createElement('span');
                      span.className = 'text-blue-600 font-semibold text-base fallback-text flex items-center gap-2';
                      span.innerHTML = `
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        Sign up with Dentulu Pro
                      `;
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>
            </a>
          </div>

          {/* Browse Marketplace Link */}
          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1">
              Browse Marketplace
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {/* Footer 
          <div className="text-center text-xs text-gray-500 mt-8 pt-6 border-t">
            <p className="mb-2">
              Powered by Dentulu © 2025 |{' '}
              <a href="https://www.dentulu.com" className="hover:underline">www.dentulu.com</a>
              {' '}
              <a href="https://www.dentulupro.com" className="hover:underline">www.dentulupro.com</a>
              {' '}
              <a href="mailto:support@dentulu.com" className="hover:underline">support@dentulu.com</a>
              {' '}
              <a href="tel:+18889050226" className="hover:underline">1.888.905.0226</a>
            </p>
          </div>*/}
        </div>
      </div>
      <Footer />
    </>
  );
}